import { useLazyQuery } from '@apollo/client';
import { Checkbox } from '@arcadiapower/shrike';
import { useState, useRef, PropsWithChildren, useEffect } from 'react';
import '@reach/dialog/styles.css';
import { Button } from '~/components/button';
import { FETCH_PERSONALIZED_CONTRACT } from '~/components/contract-link/contract-link.component';
import { Container } from '~/components/layout';
import { TermsText } from '~/components/terms/terms-text';
import { Description, Title } from '~/components/typography';
import { useDialog } from '~/hooks';
import { ContractVersion } from '~/typings/context';
import {
  Flex_FetchPersonalizedContractQuery,
  Flex_FetchPersonalizedContractQueryVariables,
} from '~/typings/graphql';
import { trackError } from '~/utils/tracking';
import { ScrollablePdf } from './scrollable-pdf.component';
import {
  Body,
  ButtonContainer,
  CloseButton,
  CloseIcon,
  Content,
  Overlay,
  Main,
} from './scrollable-terms-dialog.style';

type ContractType = 'disclosure' | 'data_consent' | 'direct';

export interface ScrollableTermsDialogProps {
  onSubmit: () => void;
  pending: boolean;
  contracts: ContractVersion[];
  showPersonalizedContractPreview?: boolean;
  utilityAccountId?: number;
  consolidateTermsText?: boolean;
  fetchPersonalizedContractForContractType?: Array<ContractType>;
}

// Depending on the requirement we can probably move this to the constants file
// But for now this is required only in this file for sorting the contracts
const contractWeights = {
  disclosure: 1,
  direct: 2,
  vnm: 3,
  agency: 4,
};

export const ScrollableTermsDialog = ({
  children,
  contracts,
  onSubmit,
  pending,
  utilityAccountId,
  consolidateTermsText = true,
  fetchPersonalizedContractForContractType,
  showPersonalizedContractPreview = false,
}: PropsWithChildren<ScrollableTermsDialogProps>) => {
  const [checked, setChecked] = useState<boolean[]>(contracts.map(() => false));
  const [checkedAll, setCheckedAll] = useState(false);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const { isOpen, close, TargetComponent } = useDialog(<span>{children}</span>);
  const scrollableContracts = contracts
    .filter(c => c.scrollRequired)
    .sort((a, b) => {
      // Sort by contract type first, then by display name
      // If the contract type is not in the contractWeights object, it will be sorted to the end
      const weightA = contractWeights[a.contractType] || Number.MAX_SAFE_INTEGER;
      const weightB = contractWeights[b.contractType] || Number.MAX_SAFE_INTEGER;

      if (weightA !== weightB) {
        return weightA - weightB;
      }

      return a.displayName.localeCompare(b.displayName);
    });
  const nonScrollableContracts = contracts
    .filter(c => !c.scrollRequired)
    .sort((a, b) => a.id - b.id);
  const [modifiedContracts, setModifiedContracts] =
    useState<ContractVersion[]>(scrollableContracts);
  const [getPersonalizedFile] = useLazyQuery<
    Flex_FetchPersonalizedContractQuery,
    Flex_FetchPersonalizedContractQueryVariables
  >(FETCH_PERSONALIZED_CONTRACT, {
    onCompleted: data => {
      if (data.contractVersion?.personalizedFile && data.contractVersion?.id) {
        setModifiedContracts(prev =>
          prev.map(c =>
            c.id === data.contractVersion?.id && data.contractVersion?.personalizedFile
              ? { ...c, fileUrl: data.contractVersion.personalizedFile }
              : c,
          ),
        );
      }
    },
    onError: e => {
      trackError(new Error('Error fetching personalized contract'), {
        extras: {
          error: e,
        },
      });
    },
  });

  const pdfRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const fetchContracts = async () => {
      if (showPersonalizedContractPreview && utilityAccountId) {
        for (const contract of scrollableContracts) {
          if (
            fetchPersonalizedContractForContractType?.includes(
              contract.contractType as ContractType,
            )
          ) {
            await getPersonalizedFile({
              variables: {
                contractVersionId: contract.id,
                utilityAccountId,
              },
            });
          }
        }
      }
    };

    fetchContracts();
  }, []);

  const handleScroll = e => {
    const { offsetHeight, scrollHeight, scrollTop } = e.target;
    if (offsetHeight + scrollTop >= scrollHeight) {
      setScrolledToBottom(true);
    } else if (scrolledToBottom) {
      // toggle back to starting state if user scrolls up again after scrolling down
      setScrolledToBottom(false);
    }
  };

  const scrollToBottom = () => {
    const { current } = pdfRef;
    if (!current) return;
    current.scrollTop = current.scrollHeight - current.clientHeight;
    setScrolledToBottom(true);
  };

  const isAllTermsAccepted = () => {
    if (consolidateTermsText) {
      return checkedAll;
    } else {
      return checked.every(c => c);
    }
  };
  return (
    <>
      {TargetComponent}
      <Overlay
        isOpen={isOpen}
        onDismiss={_event => {
          setScrolledToBottom(false);
          close();
        }}
        data-testid="terms-dialog-overlay"
      >
        <Body aria-label="Community solar terms and conditions">
          <ButtonContainer>
            <CloseButton
              data-testid="terms-dialog-close"
              onClick={_event => {
                setScrolledToBottom(false);
                close();
              }}
            >
              <CloseIcon />
            </CloseButton>
          </ButtonContainer>
          <Content height="100%" padding={0}>
            <Main onScroll={handleScroll} ref={pdfRef}>
              {modifiedContracts.map((contract, index) => (
                <div key={contract.id}>
                  {modifiedContracts.length === 1 && (
                    <Title>{`Agree to the ${contract.displayName ?? 'following contract'}`}</Title>
                  )}
                  {index === 0 && (
                    <Description mb={4}>
                      This agreement says you will support a community solar farm and receive
                      savings on your utility bill. You can cancel for any reason. Please scroll
                      through and check the box to participate.
                    </Description>
                  )}
                  <ScrollablePdf pdfUrl={contract.fileUrl!} />
                  {index !== modifiedContracts.length - 1 && <hr />}
                </div>
              ))}
              {getTermsSection(consolidateTermsText)}
            </Main>
            <Container margin={4}>
              <Button
                mode={!scrolledToBottom && !isAllTermsAccepted() ? 'default' : 'primary'}
                disabled={scrolledToBottom && !isAllTermsAccepted()}
                loading={pending}
                onClick={() => {
                  if (isAllTermsAccepted()) {
                    onSubmit();
                  } else {
                    scrollToBottom();
                    if (consolidateTermsText) {
                      setCheckedAll(true);
                    }
                  }
                }}
                testId="scroll_agree"
              >
                {getSubmitButtonText()}
              </Button>
            </Container>
          </Content>
        </Body>
      </Overlay>
    </>
  );
  function getSubmitButtonText() {
    if (!isAllTermsAccepted() && !scrolledToBottom) {
      return 'Click to scroll & agree';
    }
    if (modifiedContracts.length === 1) {
      return 'Submit agreement';
    }
    return 'Submit agreements';
  }

  function getTermsSection(consolidateTermsText: boolean) {
    if (consolidateTermsText) {
      return (
        <Checkbox
          checked={checkedAll}
          margin="0 0 16px 0"
          onChange={e => {
            const value = e.target.checked;
            // stops the screen from scrolling strangely when you click the checkbox
            const { current } = pdfRef;
            if (!current) return;
            current.scrollIntoView(true);
            setCheckedAll(value);
          }}
        >
          <TermsText
            contracts={[...scrollableContracts, ...nonScrollableContracts]}
            lastConjunction=" and the "
            preText="I have read the"
            postText=". By proceeding, I will be submitting my e-signature."
            utilityAccountId={utilityAccountId}
            showPersonalizedContractPreview
          />
        </Checkbox>
      );
    } else {
      return (
        <>
          {[...scrollableContracts, ...nonScrollableContracts].map((contract, index) => (
            <Checkbox
              key={contract.id}
              checked={checked[index]}
              margin="0 0 16px 0"
              onChange={e => {
                const value = e.target.checked;
                // stops the screen from scrolling strangely when you click the checkbox
                const { current } = pdfRef;
                if (!current) return;
                current.scrollIntoView(true);
                setChecked(prev => {
                  const newChecked = [...prev];
                  newChecked[index] = value;
                  return newChecked;
                });
              }}
            >
              <TermsText
                key={contract.id}
                contracts={[contract]}
                lastConjunction=" and the "
                preText="I have read the"
                postText=". By proceeding, I will be submitting my e-signature."
                utilityAccountId={utilityAccountId}
                showPersonalizedContractPreview
              />
            </Checkbox>
          ))}
        </>
      );
    }
  }
};
